import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', designation: '', organisation: '', org_type: '', email: '', phone: '', city: '' };
const ORG_TYPES = [['company', 'Company'], ['college', 'College / University'], ['startup', 'Startup'], ['other', 'Other']];

const Field = ({ id, label, value, onChange, ...rest }) => (
  <label className="block">
    <span className="block text-[11px] font-extrabold uppercase tracking-wider mb-1">{label}</span>
    <input id={id} value={value} onChange={(e) => onChange(e.target.value)} required
      className="w-full bg-white border-2 border-[#111] px-3 py-2.5 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#111] transition-shadow" data-testid={`rsvp-input-${id}`} {...rest} />
  </label>
);

export const ConvergeRsvp = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  useEffect(() => { axios.get(`${API}/api/public/converge/rsvp-status`).then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      setDone((await axios.post(`${API}/api/public/converge/rsvp`, form)).data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError((Array.isArray(d) ? `${d[0]?.loc?.slice(-1)[0]}: ${d[0]?.msg}` : d)?.replace('Value error, ', '') || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (done) {
    const wait = done.status === 'waitlisted';
    return (
      <div className={`mt-12 border-2 border-[#111] p-6 sm:p-8 shadow-[6px_6px_0_#111] ${wait ? 'bg-[#F5F3EB]' : 'bg-[#D7FF38]'}`} data-testid="rsvp-success">
        <CheckCircle2 size={28} />
        <p className="cv-display text-2xl sm:text-3xl font-extrabold uppercase mt-3">{wait ? "You're on the waitlist" : 'Seat confirmed'}</p>
        <p className="mt-2 text-sm font-medium">{wait ? "The Conclave is full right now — we'll email you if a seat opens up." : 'A confirmation email is on its way.'} RSVP ID: <b className="font-mono" data-testid="rsvp-id">{done.rsvp_id}</b></p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-12 bg-[#F5F3EB] border-2 border-[#111] shadow-[8px_8px_0_#111] p-6 sm:p-8" data-testid="rsvp-form">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-[#FF6338]">For HR leaders, TPOs & industry</p>
          <h3 className="cv-display text-3xl sm:text-5xl font-extrabold uppercase mt-1">RSVP for the Conclave</h3>
        </div>
        {status?.full && <span className="bg-[#111] text-[#D7FF38] px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider" data-testid="rsvp-full-badge">Seats full · join the waitlist</span>}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Field id="name" label="Full name" value={form.name} onChange={set('name')} autoComplete="name" minLength={2} />
        <Field id="designation" label="Designation" value={form.designation} onChange={set('designation')} placeholder="e.g. Head of Talent Acquisition" minLength={2} />
        <Field id="organisation" label="Organisation" value={form.organisation} onChange={set('organisation')} autoComplete="organization" minLength={2} />
        <label className="block">
          <span className="block text-[11px] font-extrabold uppercase tracking-wider mb-1">Organisation type</span>
          <select value={form.org_type} onChange={(e) => set('org_type')(e.target.value)} required className="w-full bg-white border-2 border-[#111] px-3 py-2.5 text-sm font-medium" data-testid="rsvp-input-org_type">
            <option value="" disabled>Select…</option>
            {ORG_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <Field id="email" label="Work email" type="email" value={form.email} onChange={set('email')} autoComplete="email" />
        <Field id="phone" label="Phone" type="tel" value={form.phone} onChange={set('phone')} autoComplete="tel" placeholder="+91 98xxx xxxxx" />
        <Field id="city" label="City" value={form.city} onChange={set('city')} autoComplete="address-level2" minLength={2} />
      </div>
      {error && <p className="mt-4 text-sm font-bold text-[#B91C1C]" role="alert" data-testid="rsvp-error">{error}</p>}
      <button type="submit" disabled={busy} className="cv-btn mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F5F3EB] border-2 border-[#111] shadow-[4px_4px_0_#FF6338] px-6 py-4 text-sm font-extrabold uppercase tracking-wider disabled:opacity-60" data-testid="rsvp-submit-btn">
        {busy && <Loader2 size={16} className="animate-spin" />} {status?.full ? 'Join the waitlist →' : 'Reserve my seat →'}
      </button>
      <p className="mt-3 text-[11px] font-medium text-[#111]/60">We'll only use these details for CONVERGE HR Conclave communication.</p>
    </form>
  );
};
